import { computed, Ref, ComputedRef } from 'vue'
import { redesignCallTypeIcons } from '@/enum/colors'
import i18n from '@/i18n'
import { Extension } from '@/types/extension'
import { Settings } from '@/types/dashboard'
import { TranslateResult } from 'vue-i18n'

// Constants
const RATIO_POSITIVE_COLOR = '#0D710D'
const RATIO_NEGATIVE_COLOR = '#FF4D4D'

// Types
export interface StatisticValue {
    icon: string
    iconColor: string
    value: string
}

export interface TagContentItem {
    iconName: string
    text: string | number
}

export interface StatisticItem {
    label: TranslateResult
    outgoing: StatisticValue
    incoming: StatisticValue
    other: StatisticValue
}

export interface FullSummaryItem {
    label: TranslateResult
    values?: StatisticValue[]
}

export interface HeaderStatisticTag {
    showAlways: boolean
    color?: string
    borderColor?: string
    tagContent: TagContentItem[]
    fullSummary: FullSummaryItem[]
}

export interface UseExtensionCardStatisticsOptions {
    extension: Ref<Extension>
    settings: Ref<Settings> | ComputedRef<Settings>
    cardsCols: Ref<number>
}

/**
 * Create a statistic value object with icon and color
 */
function createStatisticValue (icon: string, iconColor: string, value: unknown): StatisticValue {
    return {
        icon,
        iconColor,
        value: String(value)
    }
}

/**
 * Create a full summary statistic object for tooltips
 */
function createFullSummaryStatistic (
    label: TranslateResult,
    outgoingVal: unknown,
    incomingVal: unknown,
    otherVal?: unknown
): FullSummaryItem {
    const values = [
        createStatisticValue(redesignCallTypeIcons.outgoing, redesignCallTypeIcons.outgoingColor, outgoingVal),
        createStatisticValue(redesignCallTypeIcons.incoming, redesignCallTypeIcons.incomingColor, incomingVal)
    ]

    if (otherVal !== undefined) {
        values.push(
            createStatisticValue(redesignCallTypeIcons.otherCalls, redesignCallTypeIcons.otherColor, otherVal)
        )
    }

    return {
        label,
        values
    }
}

/**
 * Create a body statistic row
 */
function createBodyStatisticRow (
    label: TranslateResult,
    outgoingVal: unknown,
    incomingVal: unknown,
    otherVal: unknown
): StatisticItem {
    return {
        label,
        outgoing: createStatisticValue(redesignCallTypeIcons.outgoing, redesignCallTypeIcons.outgoingColor, outgoingVal),
        incoming: createStatisticValue(redesignCallTypeIcons.incoming, redesignCallTypeIcons.incomingColor, incomingVal),
        other: createStatisticValue(redesignCallTypeIcons.otherCalls, redesignCallTypeIcons.otherColor, otherVal)
    }
}

/**
 * Composable for extension card statistics computation
 */
export function useExtensionCardStatistics (options: UseExtensionCardStatisticsOptions) {
    const { extension, settings, cardsCols } = options

    /**
     * Header statistics for tags display
     */
    const headerStatistics = computed<HeaderStatisticTag[]>(() => {
        const extensionStatistic = extension.value.allDataSummary
        if (!extensionStatistic) return []

        const calls: HeaderStatisticTag = {
            showAlways: true,
            tagContent: [ {
                iconName: 'Phone',
                text: extensionStatistic.TotalCallsCount
            } ],
            fullSummary: [
                createFullSummaryStatistic(
                    i18n.global.t('widget.total.calls'),
                    extensionStatistic.OutgouingCallsCount,
                    extensionStatistic.IncomingCallsCount,
                    extensionStatistic.OtherCallsCount
                )
            ]
        }

        const totalStatistic: HeaderStatisticTag = {
            showAlways: cardsCols.value <= 3,
            tagContent: [
                {
                    iconName: 'Gauge',
                    text: extensionStatistic.TotalSum
                },
                {
                    iconName: 'IconCustomLineChart',
                    text: extensionStatistic.AverageCallsPerHour
                }
            ],
            fullSummary: [
                createFullSummaryStatistic(
                    i18n.global.t('widget.total.calls'),
                    extensionStatistic.OutgouingCallsSum,
                    extensionStatistic.IncomingCallsSum
                ),
                createFullSummaryStatistic(
                    i18n.global.t('statistics.avg.call.duration'),
                    extensionStatistic.OutgouingCallsAvg,
                    extensionStatistic.IncomingCallsAVG
                )
            ]
        }

        const isPositiveRate = extensionStatistic.GrossPercentageOfEmployment >= settings.value.positivePerformanceThreshold
        const rateColor = isPositiveRate ? RATIO_POSITIVE_COLOR : RATIO_NEGATIVE_COLOR

        const rate: HeaderStatisticTag = {
            showAlways: true,
            color: rateColor,
            borderColor: rateColor,
            tagContent: [ {
                iconName: 'Star',
                text: String(extensionStatistic.GrossPercentageOfEmployment)
            } ],
            fullSummary: [ { label: i18n.global.t('statistic.avg.ratio') } ]
        }

        return [ calls, totalStatistic, rate ]
    })

    /**
     * Body statistics for summary panel
     */
    const bodyStatistics = computed<StatisticItem[]>(() => {
        const extensionStatistic = extension.value.allDataSummary
        if (!extensionStatistic) return []

        return [
            createBodyStatisticRow(
                i18n.global.t('widget.total.calls'),
                extensionStatistic.OutgouingCallsCount,
                extensionStatistic.IncomingCallsCount,
                extensionStatistic.OtherCallsCount
            ),
            createBodyStatisticRow(
                i18n.global.t('statistic.total.time'),
                extensionStatistic.OutgouingCallsSum,
                extensionStatistic.IncomingCallsSum,
                extensionStatistic.OtherCallsSum
            ),
            createBodyStatisticRow(
                i18n.global.t('statistics.avg.call.duration'),
                extensionStatistic.OutgouingCallsAvg,
                extensionStatistic.IncomingCallsAVG,
                extensionStatistic.OtherCallsAvg
            ),
            {
                label: i18n.global.t('statistic.avg.ratio'),
                outgoing: {
                    icon: '',
                    iconColor: '',
                    value: extensionStatistic.GrossPercentageOfEmployment + ' %'
                },
                incoming: {
                    icon: '',
                    iconColor: '',
                    value: ''
                },
                other: {
                    icon: '',
                    iconColor: '',
                    value: ''
                }
            }
        ]
    })

    /**
     * Total calls statistic for compact display
     */
    const totalCallsStatistic = computed(() =>
        bodyStatistics.value.find(item => item.label === i18n.global.t('widget.total.calls'))
    )

    return {
        headerStatistics,
        bodyStatistics,
        totalCallsStatistic
    }
}
